import React, { useState } from 'react';
import { StandardListLayout } from '@/shared/layout/StandardListLayout';
import { KpiGrid, type KpiItem } from '@/components/ui/KpiGrid';
import { DataFilterToolbar, type FilterDefinition } from '@/components/ui/DataFilterToolbar';
import { Card, Avatar } from '@/components/ui';
import { ApprovalStatusBadge } from './components/ApprovalStatusBadge';
import { useApprovals } from './hooks/useApprovals';
import { useAuth } from '@/core/auth/AuthContext';
import { 
  Users, 
  Clock, 
  CheckCircle2, 
  XCircle, 
  Clock4,
  Filter as FilterIcon,
  RefreshCw,
  MoreHorizontal,
  X,
  FileText,
  Layers,
  History,
  Paperclip,
  CalendarDays
} from 'lucide-react';
import type { ApprovalRequest } from './types/approvals.types';

const PriorityBadge = ({ priority }: { priority: string }) => {
  const pMap: Record<string, { label: string; className: string }> = {
    'high': { label: 'Cao', className: 'text-rose-600 bg-rose-50' },
    'normal': { label: 'Trung bình', className: 'text-amber-600 bg-amber-50' },
    'low': { label: 'Thấp', className: 'text-emerald-600 bg-emerald-50' },
    'urgent': { label: 'Khẩn cấp', className: 'text-red-700 bg-red-100' },
  };
  const config = pMap[priority] || pMap['normal'];
  return (
    <span className={`px-2 py-0.5 rounded-md sm:text-sm text-xs whitespace-nowrap ${config.className}`}>
      {config.label}
    </span>
  );
};

export default function ApprovalsListPage() {
  const [activeTab, setActiveTab] = useState('thi_cong');
  const [selectedRequest, setSelectedRequest] = useState<ApprovalRequest | null>(null);
  const [viewMode, setViewMode] = useState<'table' | 'card' | 'compact'>('table');

  // MOCK DATA để hoàn thiện UI theo thiết kế
  const mockRequests: ApprovalRequest[] = [
    {
      id: '1',
      request_code: 'TC-REQ-2024-053',
      request_title: 'Phê duyệt biện pháp thi công',
      description: 'Hạng mục: Lát đá vỉa hè',
      project: { id: 'p1', name: 'Khu đô thị Green City' },
      entity_type: 'Biện pháp thi công',
      requester: { id: 'u1', full_name: 'Trần Minh Đức', avatar_url: '' },
      requested_at: '2024-05-22T10:30:00',
      priority_code: 'high',
      status_code: 'pending',
      company_id: '',
      module_code: 'construction',
      entity_id: '',
      current_step_order: 1,
      total_steps: 2,
      created_at: '',
      updated_at: '',
      // @ts-ignore
      _role: 'Chỉ huy trưởng',
      _dueDate: '24/05/2024'
    },
    {
      id: '2',
      request_code: 'TC-REQ-2024-052',
      request_title: 'Phê duyệt vật liệu đợt 2 - Thép xây dựng',
      description: '',
      project: { id: 'p2', name: 'Công viên Trung tâm' },
      entity_type: 'Vật liệu',
      requester: { id: 'u2', full_name: 'Phạm Văn Tài', avatar_url: '' },
      requested_at: '2024-05-22T09:15:00',
      priority_code: 'normal',
      status_code: 'pending',
      company_id: '', module_code: 'construction', entity_id: '', current_step_order: 1, total_steps: 2, created_at: '', updated_at: '',
      // @ts-ignore
      _role: 'Kỹ sư QA/QC',
      _dueDate: '24/05/2024'
    },
    {
      id: '3',
      request_code: 'TC-REQ-2024-051',
      request_title: 'Phê duyệt hồ sơ nghiệm thu',
      description: 'Hạng mục: Hệ thống điện',
      project: { id: 'p3', name: 'Tòa nhà Office Building' },
      entity_type: 'Nghiệm thu',
      requester: { id: 'u3', full_name: 'Lê Hoàng Nam', avatar_url: '' },
      requested_at: '2024-05-21T16:45:00',
      priority_code: 'high',
      status_code: 'pending',
      company_id: '', module_code: 'construction', entity_id: '', current_step_order: 1, total_steps: 2, created_at: '', updated_at: '',
      // @ts-ignore
      _role: 'Chỉ huy trưởng',
      _dueDate: '23/05/2024'
    },
    {
      id: '4',
      request_code: 'TC-REQ-2024-050',
      request_title: 'Đề xuất thay đổi thiết kế',
      description: 'Tường chắn tầng hầm',
      project: { id: 'p1', name: 'Khu đô thị Green City' },
      entity_type: 'Thay đổi thiết kế',
      requester: { id: 'u4', full_name: 'Nguyễn Văn An', avatar_url: '' },
      requested_at: '2024-05-21T11:20:00',
      priority_code: 'high',
      status_code: 'pending',
      company_id: '', module_code: 'construction', entity_id: '', current_step_order: 1, total_steps: 2, created_at: '', updated_at: '',
      // @ts-ignore
      _role: 'Quản lý dự án',
      _dueDate: '23/05/2024'
    },
    {
      id: '5',
      request_code: 'TC-REQ-2024-049',
      request_title: 'Phê duyệt kế hoạch tiến độ',
      description: 'Tháng 5/2024',
      project: { id: 'p4', name: 'Công trình trường Tiểu học A' },
      entity_type: 'Kế hoạch tiến độ',
      requester: { id: 'u5', full_name: 'Đỗ Văn Hùng', avatar_url: '' },
      requested_at: '2024-05-20T14:10:00',
      priority_code: 'normal',
      status_code: 'approved',
      company_id: '', module_code: 'construction', entity_id: '', current_step_order: 1, total_steps: 2, created_at: '', updated_at: '',
      // @ts-ignore
      _role: 'Chỉ huy trưởng',
      _dueDate: '22/05/2024'
    },
    {
      id: '6',
      request_code: 'TC-REQ-2024-048',
      request_title: 'Phê duyệt thanh toán đợt 3',
      description: 'Nhà thầu phụ ABC',
      project: { id: 'p3', name: 'Tòa nhà Office Building' },
      entity_type: 'Thanh toán',
      requester: { id: 'u6', full_name: 'Trịnh Văn Lâm', avatar_url: '' },
      requested_at: '2024-05-20T10:05:00',
      priority_code: 'normal',
      status_code: 'approved',
      company_id: '', module_code: 'construction', entity_id: '', current_step_order: 1, total_steps: 2, created_at: '', updated_at: '',
      // @ts-ignore
      _role: 'Quản lý dự án',
      _dueDate: '22/05/2024'
    },
    {
      id: '7',
      request_code: 'TC-REQ-2024-047',
      request_title: 'Phê duyệt hồ sơ hoàn công',
      description: 'Hạng mục: Cấp thoát nước',
      project: { id: 'p2', name: 'Công viên Trung tâm' },
      entity_type: 'Hoàn công',
      requester: { id: 'u7', full_name: 'Lê Thị Mai', avatar_url: '' },
      requested_at: '2024-05-19T17:30:00',
      priority_code: 'high',
      status_code: 'rejected',
      company_id: '', module_code: 'construction', entity_id: '', current_step_order: 1, total_steps: 2, created_at: '', updated_at: '',
      // @ts-ignore
      _role: 'Kỹ sư hiện trường',
      _dueDate: '21/05/2024'
    },
    {
      id: '8',
      request_code: 'TC-REQ-2024-046',
      request_title: 'Phê duyệt vật liệu thay thế',
      description: 'Ống HDPE',
      project: { id: 'p1', name: 'Khu đô thị Green City' },
      entity_type: 'Vật liệu',
      requester: { id: 'u8', full_name: 'Hoàng Văn Tùng', avatar_url: '' },
      requested_at: '2024-05-19T09:40:00',
      priority_code: 'low',
      status_code: 'rejected',
      company_id: '', module_code: 'construction', entity_id: '', current_step_order: 1, total_steps: 2, created_at: '', updated_at: '',
      // @ts-ignore
      _role: 'Chỉ huy trưởng',
      _dueDate: '21/05/2024'
    },
    {
      id: '9',
      request_code: 'TC-REQ-2024-045',
      request_title: 'Phê duyệt biện pháp thi công',
      description: 'Hạng mục: Móng',
      project: { id: 'p4', name: 'Công trình trường Tiểu học A' },
      entity_type: 'Biện pháp thi công',
      requester: { id: 'u9', full_name: 'Phạm Văn Bình', avatar_url: '' },
      requested_at: '2024-05-18T15:20:00',
      priority_code: 'high',
      status_code: 'expired',
      company_id: '', module_code: 'construction', entity_id: '', current_step_order: 1, total_steps: 2, created_at: '', updated_at: '',
      // @ts-ignore
      _role: 'Kỹ sư hiện trường',
      _dueDate: '20/05/2024'
    },
    {
      id: '10',
      request_code: 'TC-REQ-2024-044',
      request_title: 'Đề xuất thay đổi vật liệu',
      description: 'Gạch ốp tường',
      project: { id: 'p3', name: 'Tòa nhà Office Building' },
      entity_type: 'Thay đổi vật liệu',
      requester: { id: 'u10', full_name: 'Trần Thị Hoa', avatar_url: '' },
      requested_at: '2024-05-18T11:05:00',
      priority_code: 'normal',
      status_code: 'expired',
      company_id: '', module_code: 'construction', entity_id: '', current_step_order: 1, total_steps: 2, created_at: '', updated_at: '',
      // @ts-ignore
      _role: 'Kỹ sư thiết kế',
      _dueDate: '20/05/2024'
    }
  ];

  const requests = mockRequests;
  const isLoading = false;

  const totalReq = requests.length;
  const pendingReq = requests.filter(r => r.status_code === 'pending').length;
  const approvedReq = requests.filter(r => r.status_code === 'approved').length;
  const rejectedReq = requests.filter(r => r.status_code === 'rejected').length;
  const expiredReq = requests.filter(r => r.status_code === 'expired').length;

  const kpis: KpiItem[] = [
    {
      title: 'Tất cả',
      value: totalReq,
      suffix: 'Yêu cầu',
      icon: Users,
      color: 'text-blue-600',
      bg: 'bg-blue-50',
      trend: 'neutral',
      trendValue: 'Tổng quan',
      chartColor: 'bg-blue-500',
      chartData: [5, 5, 5, 5, 5]
    },
    {
      title: 'Chờ phê duyệt',
      value: pendingReq,
      suffix: 'Yêu cầu',
      icon: Clock,
      color: 'text-amber-500',
      bg: 'bg-amber-50',
      trend: 'up',
      trendValue: 'Đang chờ',
      chartColor: 'bg-amber-500',
      chartData: [2, 3, 4, 5, 4]
    },
    {
      title: 'Đã phê duyệt',
      value: approvedReq,
      suffix: 'Yêu cầu',
      icon: CheckCircle2,
      color: 'text-emerald-600',
      bg: 'bg-emerald-50',
      trend: 'up',
      trendValue: 'Hoàn thành',
      chartColor: 'bg-emerald-500',
      chartData: [4, 5, 6, 7, 8]
    },
    {
      title: 'Đã từ chối',
      value: rejectedReq,
      suffix: 'Yêu cầu',
      icon: XCircle,
      color: 'text-rose-600',
      bg: 'bg-rose-50',
      trend: 'down',
      trendValue: 'Cần xử lý',
      chartColor: 'bg-rose-500',
      chartData: [1, 1, 0, 2, 1]
    },
    {
      title: 'Quá hạn',
      value: expiredReq,
      suffix: 'Yêu cầu',
      icon: Clock4,
      color: 'text-purple-600',
      bg: 'bg-purple-50',
      trend: 'down',
      trendValue: 'Cảnh báo',
      chartColor: 'bg-purple-500',
      chartData: [0, 0, 1, 1, 2]
    },
    {
      title: 'Cần bổ sung',
      value: 0,
      suffix: 'Yêu cầu',
      icon: FileText,
      color: 'text-orange-600',
      bg: 'bg-orange-50',
      trend: 'neutral',
      trendValue: 'Phản hồi',
      chartColor: 'bg-orange-500',
      chartData: [0, 0, 0, 0, 0]
    }
  ];

  const filterDefs: FilterDefinition[] = [
    {
      id: 'tab',
      label: 'Danh mục',
      options: [
        { value: 'all', label: 'Tất cả' },
        { value: 'thi_cong', label: 'Thi công' },
        { value: 'bao_duong', label: 'Bảo dưỡng' }
      ]
    },
    {
      id: 'status',
      label: 'Tất cả trạng thái',
      options: [
        { value: 'pending', label: 'Chờ phê duyệt' },
        { value: 'approved', label: 'Đã phê duyệt' },
        { value: 'rejected', label: 'Từ chối' }
      ]
    }
  ];

  const renderTable = () => (
    <div className="flex flex-col h-full bg-white">
      <div className="flex-1 overflow-auto">
        <table className="w-full text-left border-collapse min-w-[1000px]">
          <thead className="bg-slate-50 sm:text-sm text-xs text-slate-500 sticky top-0 z-10 border-b border-slate-200 font-normal">
            <tr>
              <th className="p-3 w-10 font-normal"><input type="checkbox" className="rounded-md border-slate-300 text-emerald-600 focus:ring-emerald-500" /></th>
              <th className="p-3 whitespace-nowrap font-normal">Mã yêu cầu</th>
              <th className="p-3 min-w-[200px] font-normal">Tiêu đề</th>
              <th className="p-3 whitespace-nowrap font-normal">Dự án</th>
              <th className="p-3 whitespace-nowrap font-normal">Loại yêu cầu</th>
              <th className="p-3 min-w-[150px] font-normal">Người gửi</th>
              <th className="p-3 whitespace-nowrap font-normal">Ngày gửi</th>
              <th className="p-3 whitespace-nowrap font-normal">Hạn phê duyệt</th>
              <th className="p-3 whitespace-nowrap text-center font-normal">Ưu tiên</th>
              <th className="p-3 whitespace-nowrap font-normal">Trạng thái</th>
              <th className="p-3 w-10 font-normal"></th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100 sm:text-sm text-xs">
            {requests.map(item => (
              <tr 
                key={item.id} 
                className={`hover:bg-slate-50 cursor-pointer transition-colors ${selectedRequest?.id === item.id ? 'bg-emerald-50/50' : ''}`}
                onClick={() => setSelectedRequest(item)}
              >
                <td className="p-3" onClick={(e) => e.stopPropagation()}>
                  <input type="checkbox" className="rounded-md border-slate-300 text-emerald-600 focus:ring-emerald-500" />
                </td>
                <td className="p-3 text-blue-600">{item.request_code}</td>
                <td className="p-3">
                  <p className="text-slate-900 line-clamp-1">{item.request_title}</p>
                  {item.description && <p className="text-slate-500 sm:text-sm text-xs mt-0.5 line-clamp-1">{item.description}</p>}
                </td>
                <td className="p-3 text-slate-600">{item.project?.name || '---'}</td>
                <td className="p-3 text-slate-600">
                  {item.entity_type}
                </td>
                <td className="p-3">
                  <div className="flex items-center gap-2.5">
                    <Avatar src={item.requester?.avatar_url} name={item.requester?.full_name} size="xs" />
                    <div>
                      <p className="text-slate-900">{item.requester?.full_name}</p>
                      {/* @ts-ignore */}
                      <p className="sm:text-sm text-xs text-slate-500">{item._role || 'Chỉ huy trưởng'}</p>
                    </div>
                  </div>
                </td>
                <td className="p-3 text-slate-600 whitespace-nowrap">
                  {item.requested_at.split('T')[0].split('-').reverse().join('/')} <br/>
                  <span className="sm:text-sm text-xs text-slate-400">{item.requested_at.split('T')[1].substring(0,5)}</span>
                </td>
                <td className="p-3 text-rose-500 whitespace-nowrap">
                  {/* @ts-ignore */}
                  {item._dueDate}
                </td>
                <td className="p-3 text-center">
                  <PriorityBadge priority={item.priority_code} />
                </td>
                <td className="p-3">
                  <ApprovalStatusBadge status={item.status_code} />
                </td>
                <td className="p-3 text-slate-400">
                  <MoreHorizontal className="w-5 h-5" />
                </td>
              </tr>
            ))}
            {requests.length === 0 && !isLoading && (
              <tr>
                <td colSpan={11} className="p-8 text-center text-slate-500">Không có dữ liệu</td>
              </tr>
            )}
            {isLoading && (
              <tr>
                <td colSpan={11} className="p-8 text-center text-slate-500">Đang tải...</td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
      
      <div className="p-3 border-t border-slate-200 flex justify-between items-center bg-white sm:text-sm text-xs text-slate-500 shrink-0">
        <span>Hiển thị 1 - 10 trong tổng số {totalReq} yêu cầu</span>
        <div className="flex items-center gap-1.5">
          <button className="w-7 h-7 flex items-center justify-center rounded-md hover:bg-slate-100 text-slate-400">&lt;</button>
          <button className="w-7 h-7 flex items-center justify-center rounded-md bg-emerald-50 text-emerald-600">1</button>
          <button className="w-7 h-7 flex items-center justify-center rounded-md hover:bg-slate-100 text-slate-600">2</button>
          <button className="w-7 h-7 flex items-center justify-center rounded-md hover:bg-slate-100 text-slate-600">3</button>
          <button className="w-7 h-7 flex items-center justify-center rounded-md hover:bg-slate-100 text-slate-400">&gt;</button>
          
          <div className="flex items-center gap-2 ml-4">
            <select className="border border-slate-200 rounded-md p-3 focus:outline-none text-slate-600 bg-white">
              <option>10 / trang</option>
              <option>20 / trang</option>
            </select>
          </div>
        </div>
      </div>
    </div>
  );

  const renderSidePanel = () => {
    if (!selectedRequest) return null;
    return (
      <div className="w-[420px] flex flex-col h-full bg-white flex-shrink-0">
        <div className="flex justify-between items-center p-3 border-b border-slate-100">
          <h3 className="text-slate-900 text-base">Chi tiết yêu cầu</h3>
          <button onClick={() => setSelectedRequest(null)} className="text-slate-400 hover:text-slate-600 p-3 rounded-md hover:bg-slate-100 transition-colors">
            <X className="w-4 h-4" />
          </button>
        </div>
        
        <div className="flex-1 overflow-y-auto p-3 space-y-2">
          <div className="flex justify-between items-start mb-4">
            <span className="text-blue-600 sm:text-sm text-xs bg-blue-50 p-3 rounded-md">{selectedRequest.request_code}</span>
            <ApprovalStatusBadge status={selectedRequest.status_code} />
          </div>
          
          <div className="space-y-2">
            <div className="mb-4">
              <div className="flex items-center gap-2 text-slate-500 mb-1.5">
                <FileText className="w-4 h-4" />
                <span className="sm:text-sm text-xs">Tiêu đề</span>
              </div>
              <p className="text-slate-900 sm:text-sm text-xs leading-snug">{selectedRequest.request_title}</p>
              {selectedRequest.description && <p className="text-slate-500 sm:text-sm text-xs mt-1">{selectedRequest.description}</p>}
            </div>
            
            <div className="mb-4">
              <div className="flex items-center gap-2 text-slate-500 mb-1.5">
                <Layers className="w-4 h-4" />
                <span className="sm:text-sm text-xs">Dự án</span>
              </div>
              <p className="text-slate-900 sm:text-sm text-xs">{selectedRequest.project?.name || 'Khu đô thị Green City'}</p>
            </div>
            
            <div className="mb-4">
              <div className="flex items-center gap-2 text-slate-500 mb-1.5">
                <div className="w-4 h-4 rounded-md border-2 border-current flex items-center justify-center sm:text-sm text-xs">!</div>
                <span className="sm:text-sm text-xs">Loại yêu cầu</span>
              </div>
              <p className="text-slate-900 sm:text-sm text-xs">
                {selectedRequest.entity_type}
              </p>
            </div>
            
            <div className="mb-4">
              <div className="flex items-center gap-2 text-slate-500 mb-2">
                <Users className="w-4 h-4" />
                <span className="sm:text-sm text-xs">Người gửi</span>
              </div>
              <div className="flex items-center gap-3">
                <Avatar src={selectedRequest.requester?.avatar_url} name={selectedRequest.requester?.full_name} size="sm" />
                <div>
                  <p className="text-slate-900 sm:text-sm text-xs">{selectedRequest.requester?.full_name}</p>
                  {/* @ts-ignore */}
                  <p className="sm:text-sm text-xs text-slate-500">{selectedRequest._role || 'Chỉ huy trưởng'}</p>
                </div>
              </div>
            </div>
            
            <div className="grid grid-cols-2 gap-4 bg-slate-50 p-3 rounded-md border border-slate-100 mb-4">
              <div>
                <div className="flex items-center gap-2 text-slate-500 mb-1">
                  <Clock className="w-3.5 h-3.5" />
                  <span className="sm:text-sm text-xs">Ngày gửi</span>
                </div>
                <p className="text-slate-900 sm:text-sm text-xs">{selectedRequest.requested_at.split('T')[0].split('-').reverse().join('/')} {selectedRequest.requested_at.split('T')[1].substring(0,5)}</p>
              </div>
              <div>
                <div className="flex items-center gap-2 text-slate-500 mb-1">
                  <Clock className="w-3.5 h-3.5" />
                  <span className="sm:text-sm text-xs">Hạn phê duyệt</span>
                </div>
                {/* @ts-ignore */}
                <p className="text-rose-600 sm:text-sm text-xs">{selectedRequest._dueDate}</p>
              </div>
            </div>
            
            <div className="mb-4">
              <div className="flex items-center gap-2 text-slate-500 mb-1.5">
                <FileText className="w-4 h-4" />
                <span className="sm:text-sm text-xs">Mô tả</span>
              </div>
              <div className="text-slate-600 bg-slate-50 p-3 rounded-md border border-slate-100 sm:text-sm text-xs leading-relaxed">
                {selectedRequest.description || `Đề nghị phê duyệt biện pháp thi công hạng mục Lát đá vỉa hè tuyến đường N1 - N2 trong dự án. Kính mong ban quản lý xem xét và phản hồi sớm.`}
              </div>
            </div>
            
            <div className="mb-4">
              <div className="flex justify-between items-center mb-2.5">
                <div className="flex items-center gap-2 text-slate-500">
                  <Paperclip className="w-4 h-4" />
                  <span className="sm:text-sm text-xs">Tệp đính kèm (3)</span>
                </div>
              </div>
              <div className="space-y-2">
                <div className="flex items-center justify-between p-3 rounded-md bg-white border border-slate-200 group hover:border-emerald-300 transition-colors cursor-pointer">
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 bg-rose-50 text-rose-500 rounded-md flex items-center justify-center sm:text-sm text-xs">PDF</div>
                    <span className="sm:text-sm text-xs text-slate-700 group-hover:text-emerald-600 transition-colors">Bien_phap_thi_cong.pdf</span>
                  </div>
                  <span className="sm:text-sm text-xs text-slate-400">2.4 MB</span>
                </div>
              </div>
              <button className="text-emerald-600 sm:text-sm text-xs mt-3 w-full text-center hover:underline">Xem tất cả</button>
            </div>
            
            <div>
              <div className="flex items-center gap-2 text-slate-500 mb-4">
                <History className="w-4 h-4" />
                <span className="sm:text-sm text-xs">Lịch sử xử lý</span>
              </div>
              <div className="relative pl-3.5 space-y-2 before:absolute before:left-[17px] before:top-2 before:bottom-2 before:w-px before:bg-slate-200">
                <div className="relative pl-6">
                  <div className="absolute left-[-5px] top-1 w-2.5 h-2.5 rounded-full bg-slate-300 ring-4 ring-white"></div>
                  <p className="sm:text-sm text-xs text-slate-400 mb-0.5">{new Date(selectedRequest.requested_at).toLocaleString('vi-VN', {dateStyle: 'short', timeStyle: 'short'})}</p>
                  <p className="sm:text-sm text-xs text-slate-700"><span className="text-slate-900">{selectedRequest.requester?.full_name}</span> đã gửi yêu cầu</p>
                </div>
                <div className="relative pl-6">
                  <div className="absolute left-[-5px] top-1 w-2.5 h-2.5 rounded-full border-2 border-emerald-500 bg-white ring-4 ring-white"></div>
                  <p className="sm:text-sm text-xs text-slate-400 mb-0.5">{new Date(new Date().getTime() - 1000000).toLocaleString('vi-VN', {dateStyle: 'short', timeStyle: 'short'})}</p>
                  <p className="sm:text-sm text-xs text-slate-700"><span className="text-slate-900">Nguyễn Văn An</span> tiếp nhận xử lý</p>
                </div>
              </div>
            </div>
          </div>
        </div>
        
        <div className="p-3 border-t border-slate-100 bg-white flex gap-3 shrink-0">
          <button className="flex-1 p-3 border-2 border-rose-100 text-rose-600 rounded-md hover:bg-rose-50 hover:border-rose-200 transition-colors sm:text-sm text-xs font-bold">
            Từ chối
          </button>
          <button className="flex-1 p-3 bg-emerald-600 text-white rounded-md hover:bg-emerald-700 transition-all sm:text-sm text-xs font-bold">
            Phê duyệt
          </button>
        </div>
      </div>
    );
  };

  return (
    <StandardListLayout
      title="Phê duyệt"
      description="Quản lý và xử lý các yêu cầu, hồ sơ cần phê duyệt"
      isLoading={isLoading}
      viewMode={viewMode}
      kpiSection={<KpiGrid kpis={kpis} />}
      toolbarSection={
        <DataFilterToolbar
          search=""
          onSearchChange={() => {}}
          searchPlaceholder="Tìm kiếm yêu cầu phê duyệt..."
          filterDefs={filterDefs}
          filterValues={{ tab: activeTab, status: 'all' }}
          onFilterChange={(key, value) => {
            if (key === 'tab') setActiveTab(value || 'all');
          }}
        />
      }
      tableSection={renderTable()}
      cardSection={
        <div className="bg-slate-50 border border-slate-200 border-dashed rounded-lg flex flex-col items-center justify-center p-8 text-center text-slate-500 text-sm h-full mx-4 mb-4">
          Giao diện thẻ (Card) chưa được hỗ trợ. Vui lòng xem trên Desktop.
        </div>
      }
      sidePanel={renderSidePanel()}
    />
  );
}
